import { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { useHistory } from "react-router-dom";
import AuthContext from "../context/AuthContext";

export default function LoginPage() {
  const { loginUser } = useContext(AuthContext);
  const navigate      = useHistory();

  const handleSubmit = (e) => {
    e.preventDefault();
    const username = e.target.username.value;
    const password = e.target.password.value;
    if (username.length > 0) loginUser(username, password);
  };

  return (
    <Page>
      <Card>
        <CardLeft>
          <LeftTitle>La Petite Charrue</LeftTitle>
          <LeftSub>Auberge du Terroir</LeftSub>
          <GoldBar />
          <LeftQuote>
            <em>"Comtois rends-toi,<br />Nenni ma foi."</em>
          </LeftQuote>
        </CardLeft>

        <CardRight>
          <FormEyebrow>Administration</FormEyebrow>
          <FormTitle>Connexion</FormTitle>
          <FormSub>Accès réservé au personnel autorisé</FormSub>

          <Form onSubmit={handleSubmit}>
            <FieldGroup>
              <Label htmlFor="username">Identifiant</Label>
              <Input
                type="text"
                id="username"
                name="username"
                placeholder="Nom d'utilisateur"
                autoComplete="username"
                required
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="password">Mot de passe</Label>
              <Input
                type="password"
                id="password"
                name="password"
                placeholder="••••••••"
                autoComplete="current-password"
                required
              />
            </FieldGroup>

            <SubmitBtn type="submit">Se connecter</SubmitBtn>
          </Form>

          <BackLink onClick={() => navigate.push("/")}>
            ← Retour au site
          </BackLink>
        </CardRight>
      </Card>
    </Page>
  );
}

/* ─── Keyframes ─── */
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── Styled Components ─── */

const Page = styled.div`
  min-height: 100dvh;
  background: var(--bg-base);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
`;

const Card = styled.div`
  display: flex;
  width: 100%;
  max-width: 860px;
  min-height: 480px;
  border-radius: var(--radius-lg);
  overflow: hidden;
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  animation: ${fadeUp} 0.7s ease both;

  @media (max-width: 680px) {
    flex-direction: column;
    max-width: 420px;
  }
`;

const CardLeft = styled.div`
  flex: 1;
  background: linear-gradient(160deg, var(--secondary) 0%, #400010 100%);
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.75rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  }

  @media (max-width: 680px) {
    padding: 2.5rem 2rem;
    text-align: center;
    align-items: center;
  }
`;

const LeftTitle = styled.h2`
  font-family: var(--font-heading);
  font-size: 1.8rem;
  color: #fff;
  font-style: italic;
  font-weight: 500;
  position: relative;
`;

const LeftSub = styled.span`
  font-family: var(--font-script);
  font-size: 0.9rem;
  color: rgba(255,255,255,0.65);
  letter-spacing: 0.14em;
  position: relative;
`;

const GoldBar = styled.div`
  width: 40px;
  height: 2px;
  background: var(--primary);
  margin: 0.5rem 0;
  position: relative;
`;

const LeftQuote = styled.p`
  font-family: var(--font-heading);
  font-size: 1.05rem;
  color: rgba(255,255,255,0.75);
  line-height: 1.7;
  position: relative;
`;

const CardRight = styled.div`
  flex: 1.2;
  background: var(--bg-surface);
  padding: 3rem 2.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0.35rem;

  @media (max-width: 680px) {
    padding: 2.5rem 2rem;
  }
`;

const FormEyebrow = styled.span`
  font-family: var(--font-script);
  font-size: 0.9rem;
  color: var(--primary);
  font-style: italic;
  letter-spacing: 0.1em;
`;

const FormTitle = styled.h1`
  font-family: var(--font-heading);
  font-size: 2rem;
  color: var(--cream);
  font-weight: 500;
  margin-bottom: 0.15rem;
`;

const FormSub = styled.p`
  font-size: 0.82rem;
  color: var(--cream-muted);
  letter-spacing: 0.04em;
  margin-bottom: 1.25rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.1rem;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const Label = styled.label`
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cream-dim);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: var(--bg-card);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  color: var(--cream);
  font-family: var(--font-body);
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.25s, box-shadow 0.25s;

  &::placeholder { color: var(--cream-muted); }

  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 3px var(--primary-glow);
  }
`;

const SubmitBtn = styled.button`
  margin-top: 0.5rem;
  width: 100%;
  padding: 0.85rem;
  background: linear-gradient(135deg, var(--primary), var(--primary-dark));
  color: var(--bg-base);
  font-family: var(--font-body);
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  border: none;
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: var(--transition);

  &:hover {
    background: linear-gradient(135deg, var(--primary-light), var(--primary));
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(200, 151, 62, 0.3);
  }

  &:active { transform: translateY(0); }
`;

const BackLink = styled.button`
  margin-top: 1.25rem;
  background: none;
  border: none;
  color: var(--cream-muted);
  font-size: 0.82rem;
  letter-spacing: 0.06em;
  cursor: pointer;
  padding: 0;
  text-align: left;
  transition: color 0.2s;

  &:hover { color: var(--primary); }
`;
